import logger from '@/utils/logger';
// IndexedDB Manager para almacenamiento offline
// Gestiona biblioteca de canciones, eventos, y listas personalizadas

const DB_NAME = 'DJTeamOfflineDB';
const DB_VERSION = 1;

// Stores (tablas)
const STORES = {
  SONGS: 'songs',
  PLAYLISTS: 'playlists',
  USER_DATA: 'userData',
  SYNC_QUEUE: 'syncQueue'
};

// Memory fallback for Safari private mode
const memoryDB = {
  songs: new Map(),
  playlists: new Map(),
  userData: new Map(),
  syncQueue: new Map()
};

class OfflineDB {
  constructor() {
    this.db = null;
    this.useMemoryFallback = false;
    this.initPromise = null;
    this.initialized = false;
  }

  // Inicializar base de datos
  async init() {
    // Si ya está inicializando, esperar
    if (this.initPromise) {
      return this.initPromise;
    }
    
    // Si ya está inicializado, retornar
    if (this.initialized) {
      return this.db;
    }
    
    this.initPromise = this._doInit();
    return this.initPromise;
  }
  
  async _doInit() {
    this.initialized = true;
    // Check if IndexedDB is available
    if (!window.indexedDB) {
      console.warn('⚠️ IndexedDB no disponible, usando memoria RAM (datos no persisten)');
      this.useMemoryFallback = true;
      return Promise.resolve(null);
    }

    // Check if we're in Safari Private Mode (IndexedDB fails silently)
    try {
      const testKey = 'indexeddb_test_' + Date.now();
      const testRequest = window.indexedDB.open(testKey);
      
      const testResult = await new Promise((resolve) => {
        let resolved = false;
        
        testRequest.onerror = () => {
          if (!resolved) {
            resolved = true;
            console.warn('⚠️ IndexedDB bloqueado (modo privado Safari), usando memoria');
            resolve(false);
          }
        };
        
        testRequest.onsuccess = () => {
          if (!resolved) {
            resolved = true;
            try {
              testRequest.result.close();
              window.indexedDB.deleteDatabase(testKey);
            } catch (e) {
              // Cleanup de test DB: si falla no es crítico, pero logueamos para debug
              logger.debug('[offlineDB] cleanup testDB failed (non-critical):', e?.message);
            }
            resolve(true);
          }
        };
        
        // Timeout for Safari private mode which may hang - but don't use fallback, just continue
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            logger.log('⏱️ IndexedDB test timeout, continuing anyway...');
            resolve(true); // Don't use fallback, just try to continue
          }
        }, 2000);
      });
      
      if (!testResult) {
        this.useMemoryFallback = true;
        return Promise.resolve(null);
      }
    } catch (e) {
      console.warn('⚠️ IndexedDB test failed:', e.message);
      // Don't immediately fallback to memory - try to open the real DB first
    }

    return new Promise((resolve, reject) => {
      try {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => {
          console.error('❌ Error al abrir IndexedDB:', request.error);
          console.warn('⚠️ Usando memoria RAM como fallback');
          this.useMemoryFallback = true;
          resolve(null); // Don't reject, use fallback
        };

        request.onsuccess = () => {
          this.db = request.result;
          
          // Handle connection errors
          this.db.onerror = (event) => {
            console.error('❌ Error de IndexedDB:', event.target.error);
          };
          
          // Handle version change (another tab opened with different version)
          this.db.onversionchange = () => {
            this.db.close();
            console.warn('⚠️ Nueva versión detectada, recargando...');
          };
          
          logger.log('✅ IndexedDB inicializado correctamente');
          resolve(this.db);
        };

        request.onupgradeneeded = (event) => {
          const db = event.target.result;

          // Store para canciones
          if (!db.objectStoreNames.contains(STORES.SONGS)) {
            const songStore = db.createObjectStore(STORES.SONGS, { keyPath: 'id' });
            songStore.createIndex('title', 'title', { unique: false });
            songStore.createIndex('artist', 'artist', { unique: false });
            songStore.createIndex('favorito', 'favorito', { unique: false });
            logger.log('✅ Store songs creado');
          }

          // Store para eventos/playlists
          if (!db.objectStoreNames.contains(STORES.PLAYLISTS)) {
            const playlistStore = db.createObjectStore(STORES.PLAYLISTS, { keyPath: 'id' });
            playlistStore.createIndex('fecha_evento', 'fecha_evento', { unique: false });
            playlistStore.createIndex('estado', 'estado', { unique: false });
            logger.log('✅ Store playlists creado');
          }

          // Store para datos de usuario
          if (!db.objectStoreNames.contains(STORES.USER_DATA)) {
            db.createObjectStore(STORES.USER_DATA, { keyPath: 'key' });
            logger.log('✅ Store userData creado');
          }

          // Store para cola de sincronización (cambios pendientes)
          if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
            const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
            syncStore.createIndex('timestamp', 'timestamp', { unique: false });
            syncStore.createIndex('type', 'type', { unique: false });
            logger.log('✅ Store syncQueue creado');
          }
        };
      } catch (error) {
        console.error('❌ IndexedDB no soportado:', error);
        console.warn('⚠️ Usando memoria RAM como fallback');
        this.useMemoryFallback = true;
        resolve(null);
      }
    });
  }

  // ========== CANCIONES ==========

  async saveSongs(songs) {
    if (this.useMemoryFallback) {
      songs.forEach(song => memoryDB.songs.set(song.id, song));
      return;
    }
    if (!this.db) await this.init();
    
    const transaction = this.db.transaction([STORES.SONGS], 'readwrite');
    const store = transaction.objectStore(STORES.SONGS);

    const promises = songs.map(song => {
      return new Promise((resolve, reject) => {
        const request = store.put(song);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });

    await Promise.all(promises);
    logger.log(`✅ ${songs.length} canciones guardadas en IndexedDB`);
  }

  async getAllSongs() {
    if (this.useMemoryFallback) {
      return Array.from(memoryDB.songs.values());
    }
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SONGS], 'readonly');
      const store = transaction.objectStore(STORES.SONGS);
      const request = store.getAll();

      request.onsuccess = () => {
        logger.log(`📚 ${request.result.length} canciones recuperadas de cache`);
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getSongById(id) {
    if (this.useMemoryFallback) {
      return memoryDB.songs.get(id) || null;
    }
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SONGS], 'readonly');
      const store = transaction.objectStore(STORES.SONGS);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async updateSong(song) {
    if (this.useMemoryFallback) {
      memoryDB.songs.set(song.id, song);
      return;
    }
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SONGS], 'readwrite');
      const store = transaction.objectStore(STORES.SONGS);
      const request = store.put(song);

      request.onsuccess = () => {
        logger.log(`✅ Canción ${song.id} actualizada en cache`);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ========== EVENTOS/PLAYLISTS ==========

  async savePlaylists(playlists) {
    // Reemplaza el contenido: así los eventos borrados en el servidor no "resucitan" offline
    if (this.useMemoryFallback) {
      memoryDB.playlists.clear();
      playlists.forEach(playlist => memoryDB.playlists.set(playlist.id, playlist));
      return;
    }
    if (!this.db) await this.init();
    
    const transaction = this.db.transaction([STORES.PLAYLISTS], 'readwrite');
    const store = transaction.objectStore(STORES.PLAYLISTS);
    await new Promise((resolve, reject) => {
      const clearRequest = store.clear();
      clearRequest.onsuccess = () => resolve();
      clearRequest.onerror = () => reject(clearRequest.error);
    });

    const promises = playlists.map(playlist => {
      return new Promise((resolve, reject) => {
        const request = store.put(playlist);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });

    await Promise.all(promises);
    logger.log(`✅ ${playlists.length} eventos guardados en IndexedDB`);
  }

  async getAllPlaylists() {
    if (this.useMemoryFallback) {
      return Array.from(memoryDB.playlists.values());
    }
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PLAYLISTS], 'readonly');
      const store = transaction.objectStore(STORES.PLAYLISTS);
      const request = store.getAll();

      request.onsuccess = () => {
        logger.log(`📅 ${request.result.length} eventos recuperados de cache`);
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getPlaylistById(id) {
    if (this.useMemoryFallback) {
      return memoryDB.playlists.get(id) || null;
    }
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PLAYLISTS], 'readonly');
      const store = transaction.objectStore(STORES.PLAYLISTS);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ========== DATOS DE USUARIO ==========

  async saveUserData(key, value) {
    if (this.useMemoryFallback) {
      memoryDB.userData.set(key, value);
      return;
    }
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.USER_DATA], 'readwrite');
      const store = transaction.objectStore(STORES.USER_DATA);
      const request = store.put({ key, value, timestamp: Date.now() });

      request.onsuccess = () => {
        logger.log(`✅ Dato ${key} guardado en cache`);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getUserData(key) {
    if (this.useMemoryFallback) {
      return memoryDB.userData.get(key) || null;
    }
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.USER_DATA], 'readonly');
      const store = transaction.objectStore(STORES.USER_DATA);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result?.value);
      request.onerror = () => reject(request.error);
    });
  }

  // ========== COLA DE SINCRONIZACIÓN ==========

  async addToSyncQueue(action) {
    if (this.useMemoryFallback) {
      const item = { id: Date.now().toString(), ...action, synced: false };
      memoryDB.syncQueue.set(item.id, item);
      return;
    }
    if (!this.db) await this.init();

    const item = {
      ...action,
      timestamp: Date.now(),
      synced: false
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SYNC_QUEUE], 'readwrite');
      const store = transaction.objectStore(STORES.SYNC_QUEUE);
      const request = store.add(item);

      request.onsuccess = () => {
        logger.log('📝 Acción agregada a cola de sincronización:', action.type);
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getSyncQueue() {
    if (this.useMemoryFallback) {
      const items = Array.from(memoryDB.syncQueue.values());
      const pendingItems = items.filter(item => !item.synced);
      return pendingItems;
    }

    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SYNC_QUEUE], 'readonly');
      const store = transaction.objectStore(STORES.SYNC_QUEUE);
      const request = store.getAll();

      request.onsuccess = () => {
        const pendingItems = request.result.filter(item => !item.synced);
        logger.log(`📤 ${pendingItems.length} acciones pendientes de sincronizar`);
        resolve(pendingItems);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async markAsSynced(id) {
    if (this.useMemoryFallback) {
      const item = memoryDB.syncQueue.get(id);
      if (item) {
        item.synced = true;
        memoryDB.syncQueue.set(id, item);
      }
      return;
    }
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SYNC_QUEUE], 'readwrite');
      const store = transaction.objectStore(STORES.SYNC_QUEUE);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const item = getRequest.result;
        if (item) {
          item.synced = true;
          item.syncedAt = Date.now();
          const updateRequest = store.put(item);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          resolve();
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async clearSyncQueue() {
    if (this.useMemoryFallback) {
      memoryDB.syncQueue.clear();
      return;
    }
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SYNC_QUEUE], 'readwrite');
      const store = transaction.objectStore(STORES.SYNC_QUEUE);
      const request = store.clear();

      request.onsuccess = () => {
        logger.log('🗑️ Cola de sincronización limpiada');
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ========== UTILIDADES ==========

  // Guardar datos en un store
  async save(storeName, data) {
    if (this.useMemoryFallback) {
      memoryDB[storeName].set(data.id, data);
      return data.id;
    }

    if (!this.db) {
      console.warn('⚠️ DB no inicializada');
      return;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Obtener dato por ID
  async get(storeName, id) {
    if (this.useMemoryFallback) {
      return memoryDB[storeName].get(id) || null;
    }

    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Obtener todos los datos
  async getAll(storeName) {
    if (this.useMemoryFallback) {
      return Array.from(memoryDB[storeName].values());
    }

    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getCacheStats() {
    if (this.useMemoryFallback) {
      return {
        songs: memoryDB.songs.size,
        playlists: memoryDB.playlists.size,
        pendingSync: memoryDB.syncQueue.size,
        lastUpdate: null
      };
    }

    if (!this.db) await this.init();

    const songs = await this.getAllSongs();
    const playlists = await this.getAllPlaylists();
    const syncQueue = await this.getSyncQueue();

    return {
      songs: songs.length,
      playlists: playlists.length,
      pendingSync: syncQueue.length,
      lastUpdate: await this.getUserData('lastCacheUpdate')
    };
  }

  async clearAllData() {
    if (this.useMemoryFallback) {
      memoryDB.songs.clear();
      memoryDB.playlists.clear();
      memoryDB.userData.clear();
      memoryDB.syncQueue.clear();
      return;
    }
    if (!this.db) await this.init();

    const stores = [STORES.SONGS, STORES.PLAYLISTS, STORES.USER_DATA, STORES.SYNC_QUEUE];
    const transaction = this.db.transaction(stores, 'readwrite');

    const promises = stores.map(storeName => {
      return new Promise((resolve, reject) => {
        const store = transaction.objectStore(storeName);
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });

    await Promise.all(promises);
    logger.log('🗑️ Todos los datos offline eliminados');
  }
}

// Singleton instance
const offlineDB = new OfflineDB();

export default offlineDB;
